import { useId } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const labelClass = "text-[13px] font-medium text-ink-900";
const hintClass = "text-[12px] text-ink-500";
const errorClass = "text-[12px] text-red-500";
const controlClass =
  "w-full rounded-md border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 placeholder:text-ink-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-dk";

/**
 * One section of the site, in the template's fixed order. `enabled` is absent
 * for the hero, which always renders (it holds the agent); a section that is
 * off keeps its fields, so turning it back on loses nothing.
 */
export function SiteSectionCard({
  title,
  description,
  enabled,
  onEnabledChange,
  children,
}: {
  title: string;
  description?: string;
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const showFields = enabled === undefined || enabled;
  return (
    <section className="rounded-lg border border-ink-100 bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-ink-900">{title}</h2>
          {description && <p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-500">{description}</p>}
        </div>
        {enabled !== undefined && onEnabledChange && (
          <Switch checked={enabled} onCheckedChange={onEnabledChange} aria-label={`Mostrar «${title}»`} />
        )}
      </div>
      {showFields ? (
        <div className="mt-4 flex flex-col gap-4">{children}</div>
      ) : (
        <p className="mt-2 text-[12.5px] text-ink-500">Oculta en el sitio. Sus campos se conservan.</p>
      )}
    </section>
  );
}

type TextFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** The schema's limit, shown as a counter — the schema is what enforces it. */
  max?: number;
  placeholder?: string;
  hint?: string;
  error?: string;
  multiline?: boolean;
  rows?: number;
  type?: "text" | "email" | "url";
};

export function SiteTextField({
  label,
  value,
  onChange,
  max,
  placeholder,
  hint,
  error,
  multiline,
  rows = 4,
  type = "text",
}: TextFieldProps): React.JSX.Element {
  const id = useId();
  const over = max !== undefined && value.length > max;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className={labelClass}>
          {label}
        </label>
        {max !== undefined && (
          <span className={`text-[11px] tabular-nums ${over ? "text-red-500" : "text-ink-500"}`}>
            {value.length}/{max}
          </span>
        )}
      </div>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          rows={rows}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
          className={controlClass}
        />
      ) : (
        <input
          id={id}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error}
          className={controlClass}
        />
      )}
      {error ? <span className={errorClass}>{error}</span> : hint && <span className={hintClass}>{hint}</span>}
    </div>
  );
}

/** A labelled switch for a boolean that is not a section's own on/off. */
export function SiteToggleField({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}): React.JSX.Element {
  const id = useId();
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <label htmlFor={id} className={labelClass}>
          {label}
        </label>
        {hint && <p className={`mt-0.5 ${hintClass}`}>{hint}</p>}
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onChange} aria-label={label} />
    </div>
  );
}

export type SiteChoice<T extends string> = { value: T; label: string };

/**
 * A short, fixed set of options side by side, one of them chosen — the site's
 * style presets. A radio group, so arrows and screen readers treat it as one.
 */
export function SiteSegmentedField<T extends string>({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  value: T;
  options: ReadonlyArray<SiteChoice<T>>;
  onChange: (value: T) => void;
}): React.JSX.Element {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <span id={id} className={labelClass}>
        {label}
      </span>
      <div role="radiogroup" aria-labelledby={id} className="flex rounded-lg bg-ink-050 p-1">
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(option.value)}
              className={`flex-1 rounded-md px-3 py-1.5 text-[13px] font-medium transition ${
                selected ? "bg-white text-ink-900 shadow-sm" : "text-ink-500 hover:text-ink-900"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {hint && <span className={hintClass}>{hint}</span>}
    </div>
  );
}

/** One of several options in a dropdown, for a set too long to lay side by side. */
export function SiteSelectField<T extends string>({
  label,
  hint,
  value,
  options,
  onChange,
}: {
  label: string;
  hint?: string;
  value: T;
  options: ReadonlyArray<SiteChoice<T>>;
  onChange: (value: T) => void;
}): React.JSX.Element {
  const id = useId();
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <Select
        value={value}
        onValueChange={(next) => {
          const chosen = options.find((o) => o.value === next);
          if (chosen) onChange(chosen.value);
        }}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <span className={hintClass}>{hint}</span>}
    </div>
  );
}
